import { useMutation, useApolloClient, gql } from '@apollo/client'
import { isEmpty } from 'lodash'
import React, { ReactElement, useState } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { Button } from 'components/v2/Button'
import { CopyToClipboard, CopyToClipboardWrapper } from '@10xLabs/react-web/components/CopyToClipboard'
import { Dropdown } from 'components/v2/Dropdown'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { CreditCardIcon } from '@10xLabs/react-web/components/Icons/CreditCard'
import { MoneyBillIcon } from '@10xLabs/react-web/components/Icons/MoneyBill'
import { StyledLink } from 'components/v2/Link'
import { CouponDetails } from 'components/CouponDetails'
import { TotalWithDiscount } from 'components/TotalWithDiscount'
import { BookingAddChargeId } from 'features/Bookings/components/AddChargeId'
import {
  resolvers as bookingsResolvers,
  isConfirmed,
  isReserved,
  hasNotPaidReturnTrip,
  getBookingAmountByTickets,
  getUnpaidTicketsForPayment
} from 'resolvers/bookings'
import { getTotalInsuranceByTickets } from 'resolvers/insurance'
import { getTotalServiceFeeByTickets } from 'resolvers/serviceFee'
import { getUnprocessableErrors } from 'utils/error'
import { sanitizePrice } from 'utils/math'
import {
  IPaymentsDetailsProps,
  IPaymentMethodProps,
  IPaymentsGroupedByListId,
  ISendTicketsPaymentProps,
  IBookingPaymentProps
} from './types'
import {
  GTBookingInvoicedBy,
  GTBookingV2Payment,
  GTBookingV2PaymentMethod,
  GTBookingV2PaymentProcessor,
  GTBookingV2PaymentStatus,
  GTPaymentMethod
} from 'gql/types.generated'
import { getTotalLuggageAmountByTickets, getTotalPreferedSeatsAmountByTickets } from 'resolvers/ancillaries'
import { safeFormatMessage } from 'utils/intl'

const SecondaryInfo = styled.div`
  font-size: 13px;
  color: #9999bd;
  white-space: nowrap;
`

const AddChargeIdWrapper = styled.div`
  margin-top: 2px;
`

const PaymentMethodSelect = styled.div`
  display: flex;

  button {
    margin: 0 5px;

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }
  }
`

const PaymentMethodIcon = styled.div`
  fill: #5d5c70;
  margin-right: 5px;
  height: 14px;
`

const PaymentsWrapper = styled.div`
  display: flex;
  align-items: center;
`

const PaymentsLabel = styled.div`
  margin-right: 3px;
`

const PaymentsCount = styled.div`
  font-size: 13px;
`

const NotPaidReturnTrip = styled.div`
  margin-top: 20px;
`

const PaymentDropdownWrapper = styled.div`
  padding: 10px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
`

interface IPaymentTagTheme {
  backgroundColor: string
  color: string
}

const PaymentTag = styled.div<IPaymentTagTheme>`
  background-color: ${({ backgroundColor }): string => backgroundColor};
  border-radius: 4px;
  color: ${({ color }): string => color};
  font-weight: 500;
  padding: 2px 4px;
  white-space: nowrap;
`

const getPaymentMethodTheme = (payment: GTBookingV2Payment): IPaymentTagTheme => {
  let theme: IPaymentTagTheme
  if (payment.method === GTBookingV2PaymentMethod.Cash) {
    theme = { backgroundColor: '#EEFFFB', color: '#00C875' }
  } else if (payment.processor === GTBookingV2PaymentProcessor.Conekta) {
    theme = { backgroundColor: '#EAEAFF', color: '#28265D' }
  } else if (payment.processor === GTBookingV2PaymentProcessor.MercadoPago) {
    theme = { backgroundColor: '#CFE7FF', color: '#1B77D2' }
  } else {
    theme = { backgroundColor: '#FBF4E6', color: '#D68E01' }
  }

  return theme
}

const PaymentMethod = ({ payment, coupon }: IPaymentMethodProps): ReactElement => {
  const theme = getPaymentMethodTheme(payment)

  switch (payment.method) {
    case GTBookingV2PaymentMethod.Cash:
      return (
        <PaymentTag {...theme}>
          <FormattedMessage id="dictionary.cash" defaultMessage="Cash" />
        </PaymentTag>
      )
    case GTBookingV2PaymentMethod.Coupon:
      return <span>{coupon?.code}</span>
    case GTBookingV2PaymentMethod.Kredit:
      return <FormattedMessage id="dictionary.kredits" defaultMessage="Kredit" />
  }

  if (payment.processor === GTBookingV2PaymentProcessor.Conekta) {
    return (
      <CopyToClipboardWrapper>
        <PaymentTag {...theme}>
          <FormattedMessage id="dictionary.conekta" defaultMessage="Conekta" />
        </PaymentTag>
        <CopyToClipboard str={payment.chargeID} />
      </CopyToClipboardWrapper>
    )
  } else if (payment.processor === GTBookingV2PaymentProcessor.MercadoPago) {
    return (
      <PaymentTag {...theme}>
        <FormattedMessage id="dictionary.mercadoPago" defaultMessage="Mercado Pago" />
      </PaymentTag>
    )
  }

  return <span>{payment.last4 || payment.chargeID || payment.authCode}</span>
}

const PaymentUser = styled.div`
  white-space: nowrap;
`

const PaymentsDetails = ({ booking }: IPaymentsDetailsProps): ReactElement => {
  const totalPayments: IPaymentsGroupedByListId[] = []
  booking.payments.forEach((payment): TSFixMe => {
    if (payment.status === GTBookingV2PaymentStatus.Paid) {
      const ticketIndex = totalPayments.findIndex(
        (processPayment): TSFixMe => processPayment.paymentListID === payment.paymentListID
      )

      if (ticketIndex !== -1) {
        totalPayments[ticketIndex].payments.push(payment)
      } else {
        totalPayments.push({
          payments: [payment],
          paymentListID: payment.paymentListID
        })
      }
    }
  })

  const pricesCurrency = booking.currency.code === 'USD' ? 'USD' : undefined
  return (
    <PaymentDropdownWrapper>
      {totalPayments.map((groupPayment, index): TSFixMe => {
        return (
          <React.Fragment key={index}>
            {groupPayment.payments.map((payment, index): TSFixMe => {
              const isKreditPayment = payment.method === GTBookingV2PaymentMethod.Kredit

              return (
                <React.Fragment key={`paymentByMethod${index}`}>
                  <div>
                    <PaymentMethod payment={payment} />
                  </div>
                  <FormattedPrice value={sanitizePrice(payment.amount)} currency={pricesCurrency} />
                  {!isKreditPayment ? (
                    <PaymentUser>
                      {!isEmpty(payment.user) ? (
                        <div>
                          {payment.user.firstName} {payment.user.lastName}
                        </div>
                      ) : (
                        <FormattedMessage id="bookings.payment.paidOnline" defaultMessage="Paid online" />
                      )}
                    </PaymentUser>
                  ) : (
                    <div />
                  )}
                </React.Fragment>
              )
            })}
          </React.Fragment>
        )
      })}
    </PaymentDropdownWrapper>
  )
}

const CONFIRM_BOOKING_PAYMENT_ON_SITE = gql`
  mutation ConfirmBookingPaymentOnSite($bookingId: ID!, $method: PaymentMethod!, $ticketIds: [ID!]!) {
    confirmBookingPaymentOnSite(input: { bookingID: $bookingId, method: $method, ticketIDs: $ticketIds })
  }
`

const UPDATE_BOOKING_PAYMENT_METHOD = gql`
  mutation UpdateBookingPaymentMethod($bookingId: ID!, $method: PaymentMethod!, $tickets: [Ticket!]!) {
    updateBookingPaymentMethod(bookingId: $bookingId, method: $method, tickets: $tickets) @client
  }
`

const getOnlineAttempts = (payments: GTBookingV2Payment[]): number => {
  return payments.reduce((result, payment): TSFixMe => {
    if (payment.processor === GTBookingV2PaymentProcessor.Conekta) {
      return result + 1
    }

    return result
  }, 0)
}

const getSuccessPaymentsCount = (payments: GTBookingV2Payment[]): number => {
  const paymentListIDs = new Set()
  return payments.reduce((result, payment): TSFixMe => {
    if (payment.status === GTBookingV2PaymentStatus.Paid && !paymentListIDs.has(payment.paymentListID)) {
      paymentListIDs.add(payment.paymentListID)
      return result + 1
    }

    return result
  }, 0)
}

const SendTicketsPayment = ({ booking, canWrite }: ISendTicketsPaymentProps): ReactElement => {
  const [isShowingAddChargeId, showAddChargeId] = useState(false)

  const [updateBookingPaymentMethod] = useMutation(UPDATE_BOOKING_PAYMENT_METHOD)
  const [confirmBookingPaymentOnSite, { loading, error }] = useMutation(CONFIRM_BOOKING_PAYMENT_ON_SITE, {
    onCompleted() {
      const unpaidTickets = getUnpaidTicketsForPayment(booking)

      updateBookingPaymentMethod({
        variables: {
          bookingId: booking.id,
          method: GTBookingV2PaymentMethod.Cash,
          tickets: unpaidTickets
        }
      })
    }
  })

  const handleCashPaymentMethod = (): void => {
    const unpaidTickets = getUnpaidTicketsForPayment(booking) || []

    confirmBookingPaymentOnSite({
      variables: {
        bookingId: booking.id,
        method: GTPaymentMethod.Cash,
        ticketIds: unpaidTickets.map(({ id }): TSFixMe => id)
      }
    })
  }

  const handleCardPaymentMethod = (): void => {
    showAddChargeId(true)
  }

  return (
    <>
      <AddChargeIdWrapper>
        {isShowingAddChargeId ? (
          <BookingAddChargeId booking={booking} />
        ) : (
          canWrite && (
            <PaymentMethodSelect>
              <Button kind="terciary" onClick={handleCashPaymentMethod} loading={loading} noIcon small>
                <PaymentMethodIcon as={MoneyBillIcon} />
                <FormattedMessage id="dictionary.cash" defaultMessage="Cash" />
              </Button>
              <Button kind="terciary" onClick={handleCardPaymentMethod} noIcon small>
                <PaymentMethodIcon as={CreditCardIcon} />
                <FormattedMessage id="dictionary.card" defaultMessage="Card" />
              </Button>
            </PaymentMethodSelect>
          )
        )}
      </AddChargeIdWrapper>
      {error && !isShowingAddChargeId && (
        <ErrorMessage errors={getUnprocessableErrors(error)}>
          <FormattedMessage
            id="bookings.setPaymentMethod.cash.error"
            defaultMessage="There was an error while saving the payment method"
          />
        </ErrorMessage>
      )}
    </>
  )
}

const NoWrap = styled.div`
  white-space: nowrap;
`

const Tag = styled.div`
  padding: 2px 6px;
  font-size: 13px;
  color: white;
  border-radius: 4px;
  text-transform: uppercase;
  margin-block-start: 5px;
  width: min-content;
`

const Reward = styled(Tag)`
  background-color: #525dc0;
`

const Invoiced = styled(Tag)`
  background-color: #6547e6;
`

const SecondaryTagInfo = styled.div`
  text-transform: none;
`

const invoicedByMessages = defineMessages({
  [GTBookingInvoicedBy.Finance]: {
    id: 'bookings.invoicedBy.finance',
    defaultMessage: 'Finance'
  },
  [GTBookingInvoicedBy.User]: {
    id: 'bookings.invoicedBy.user',
    defaultMessage: 'User'
  },
  [GTBookingInvoicedBy.Automatic]: {
    id: 'bookings.invoicedBy.automatic',
    defaultMessage: 'Automatic'
  }
})

export const BookingPayment = ({ booking, canWrite }: IBookingPaymentProps): ReactElement => {
  const intl = useIntl()
  const client = useApolloClient()
  // @ts-expect-error ts(2322) FIXME
  client.addResolvers(bookingsResolvers)
  const bookingHasReward = booking.tickets.some((ticket): TSFixMe => ticket.isRewardApplied)
  const { payments, coupon } = booking
  const { subtotal, vat, discount } = getBookingAmountByTickets(booking)
  const successPaymentsCount = getSuccessPaymentsCount(payments)
  const totalInsurance = getTotalInsuranceByTickets(booking.tickets)
  const totalLuggage = getTotalLuggageAmountByTickets(booking.tickets)
  const totalPreferedSeats = getTotalPreferedSeatsAmountByTickets(booking.tickets)
  const totalAncillaries = totalInsurance + totalLuggage + totalPreferedSeats
  const totalServiceFee = getTotalServiceFeeByTickets(booking.tickets)
  const pricesCurrency = booking.currency.code === 'USD' ? 'USD' : undefined
  const shouldDisplayBreakdown = totalInsurance > 0 || totalServiceFee > 0 || totalAncillaries > 0
  const ticketPrice = subtotal + vat - totalAncillaries - totalServiceFee

  return (
    <div>
      <TotalWithDiscount total={subtotal + vat} discount={discount} currency={pricesCurrency} />
      {shouldDisplayBreakdown && (
        <SecondaryInfo>
          <NoWrap>
            <>(</>
            <FormattedPrice value={sanitizePrice(ticketPrice)} currency={pricesCurrency} />
            &nbsp;
            <>+</>
            &nbsp;
            <FormattedPrice value={sanitizePrice(totalAncillaries)} currency={pricesCurrency} />
            &nbsp;
            <>+</>
            &nbsp;
            <FormattedPrice value={sanitizePrice(totalServiceFee)} currency={pricesCurrency} />
            <>)</>
          </NoWrap>
        </SecondaryInfo>
      )}
      {isConfirmed(booking) && (
        <SecondaryInfo>
          <FormattedMessage
            id="bookings.attempts"
            defaultMessage="Online attemps: {attempts}"
            values={{
              attempts: getOnlineAttempts(payments)
            }}
          />
        </SecondaryInfo>
      )}
      {isReserved(booking) && <SendTicketsPayment booking={booking} canWrite={canWrite} />}
      {payments.length > 0 && (
        <PaymentsWrapper>
          <PaymentsLabel>
            <SecondaryInfo>
              <FormattedMessage id="bookings.onlinePayments.count" defaultMessage="Payments:" />
            </SecondaryInfo>
          </PaymentsLabel>
          {successPaymentsCount > 0 ? (
            <Dropdown
              toggle={
                <PaymentsCount>
                  <StyledLink>{successPaymentsCount}</StyledLink>
                </PaymentsCount>
              }
              noPadding
              fitContent
            >
              <PaymentsDetails booking={booking} />
            </Dropdown>
          ) : (
            '0'
          )}
        </PaymentsWrapper>
      )}
      <CouponDetails coupon={coupon} />
      {hasNotPaidReturnTrip(booking) && (
        <NotPaidReturnTrip>
          <SecondaryInfo>
            <FormattedMessage id="bookings.payments.returnTrip" defaultMessage="Return tickets payment:" />
          </SecondaryInfo>

          <SendTicketsPayment booking={booking} canWrite={canWrite} />
        </NotPaidReturnTrip>
      )}
      {bookingHasReward && (
        <Reward>
          <FormattedMessage id="bookings.reward" defaultMessage="Reward" />
        </Reward>
      )}
      {booking.isTaxInvoiced && (
        <Invoiced>
          <FormattedMessage
            id="bookings.payments.invoiced"
            defaultMessage="Invoiced {invoicedBy}"
            values={{
              invoicedBy: (
                <>
                  {!!booking.invoicedBy && (
                    <SecondaryTagInfo>
                      ({safeFormatMessage({ intl, messages: invoicedByMessages, key: booking.invoicedBy })})
                    </SecondaryTagInfo>
                  )}
                </>
              )
            }}
          />
        </Invoiced>
      )}
    </div>
  )
}
